"use client";

import { useCallback, useState, useRef } from "react";

interface FileUploaderProps {
  accept: string;
  multiple?: boolean;
  maxSizeMB?: number;
  maxFiles?: number;
  /** The backend takes this much per request IN TOTAL (tools.py MAX_TOTAL_SIZE). */
  maxTotalMB?: number;
  onFilesSelected: (files: File[]) => void;
  label?: string;
  description?: string;
}

export default function FileUploader({
  accept,
  multiple = false,
  maxSizeMB = 50,
  maxFiles = 20,
  maxTotalMB = 50,
  onFilesSelected,
  label = "Drop your file here",
  description = "or click to browse",
}: FileUploaderProps) {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateAndAdd = useCallback(
    (newFiles: FileList | File[]) => {
      setError("");
      const arr = Array.from(newFiles);

      // Check max files
      const total = files.length + arr.length;
      if (!multiple && total > 1) {
        setError("Only 1 file allowed");
        return;
      }
      if (total > maxFiles) {
        setError(`Maximum ${maxFiles} files allowed`);
        return;
      }

      // Check size
      for (const f of arr) {
        if (f.size > maxSizeMB * 1024 * 1024) {
          setError(`"${f.name}" exceeds ${maxSizeMB}MB limit`);
          return;
        }
      }

      const updated = multiple ? [...files, ...arr] : arr;
      const totalBytes = updated.reduce((n, f) => n + f.size, 0);
      if (multiple && totalBytes > maxTotalMB * 1024 * 1024) {
        setError(`These files add up to ${(totalBytes / 1048576).toFixed(1)}MB; the limit is ${maxTotalMB}MB in total`);
        return;
      }
      setFiles(updated);
      onFilesSelected(updated);
    },
    [files, multiple, maxFiles, maxSizeMB, maxTotalMB, onFilesSelected]
  );

  const removeFile = useCallback(
    (index: number) => {
      const updated = files.filter((_, i) => i !== index);
      setFiles(updated);
      onFilesSelected(updated);
    },
    [files, onFilesSelected]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length) {
        validateAndAdd(e.dataTransfer.files);
      }
    },
    [validateAndAdd]
  );

  const reset = useCallback(() => {
    setFiles([]);
    setError("");
    onFilesSelected([]);
    if (inputRef.current) inputRef.current.value = "";
  }, [onFilesSelected]);

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200 p-8 sm:p-12 text-center ${
          dragOver
            ? "border-[var(--color-gold)] bg-[var(--color-gold-muted)]/30 scale-[1.01]"
            : "border-slate-300 hover:border-[var(--color-gold)] hover:bg-slate-50"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => e.target.files && validateAndAdd(e.target.files)}
          className="hidden"
        />

        <div className="flex flex-col items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50">
            <svg className="h-7 w-7 text-brand-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          </div>
          <div>
            <p className="text-base font-semibold text-slate-700">{label}</p>
            <p className="mt-1 text-sm text-slate-400">{description}</p>
          </div>
          <p className="text-xs text-slate-400">
            {multiple
              ? `${maxSizeMB === maxTotalMB ? `Max ${maxTotalMB}MB in total` : `Max ${maxSizeMB}MB each · ${maxTotalMB}MB in total`} · Up to ${maxFiles} files`
              : `Max ${maxSizeMB}MB`}
          </p>
        </div>
      </div>

      {error && (
        <p className="mt-3 text-sm text-red-600 flex items-center gap-1.5">
          <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          {error}
        </p>
      )}

      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          {files.map((f, i) => (
            <div
              key={`${f.name}-${i}`}
              className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5"
            >
              <svg className="h-5 w-5 text-red-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 truncate">{f.name}</p>
                <p className="text-xs text-slate-400">{(f.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                className="rounded-md p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
